import { Component, ElementRef, OnInit, signal, ViewChild, effect, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PropertySearchFilter } from '../../../../features/property/components/property-search-filter/property-search-filter';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { RecipientSelectorBaseClass, RecipientSelectionState } from '../../../../core/base-classes/recipient-selector-base-class';
import { PropertyModel } from '../../../../core/models/data/property.model';
import { PropertiesFilter } from '../../../../core/filters/properties.filter';
import { PropertiesService } from '../../../../core/services/property.service';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { AddressTypes, AddressVerificationStatus, PropertyMemberTypes, PropertyStatuses, SelectionPrimaryKeys } from '../../../../core/enums/data.enums';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { PaginationConstants } from '../../../../core/constants/constants';
import { PropertyListSortOptions } from '../../../../core/enums/sort-options.enums';
import { MobileCardCheckbox } from '../../../../parts/mobile-card-checkbox/mobile-card-checkbox';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';

export type PropertySelectionState = RecipientSelectionState<PropertiesFilter>;

@Component({
  selector: 'ms-charge-schedule-property-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    PropertySearchFilter,
    SortDropdownComponent,
    NoValuePipe,
    PaginationComponent,
    InViewPortDirective,
    MobileCardCheckbox,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective
  ],
  templateUrl: './charge-schedule-property-selector.component.html',
  styleUrl: './charge-schedule-property-selector.component.scss'
})
export class ChargeSchedulePropertySelectorComponent extends RecipientSelectorBaseClass<PropertiesFilter> implements OnInit {

  //#region Properties & Signals

  public organizationId = input.required<number>();
  public initialFilter = input.required<PropertiesFilter>();
  
  public selectionChanged = output<PropertySelectionState>();

  public propertyList = signal<PropertyModel[]>([]);
  public isLoadingProperties = false;
  public searchTerm = '';
  
  // Defaulted (not just declared) so it's a valid object before ngOnInit runs — the constructor's
  // emit effect can fire its first run before ngOnInit assigns the real initialFilter().
  public propertiesFilter: PropertiesFilter = ListConstants.PROPERTIES_FILTER_DEFAULT();
  public propertyListSortOptions = PropertyListSortOptions;
  public skeletonProperties: PropertyModel[] = [];

  public SelectionPrimaryKeys = SelectionPrimaryKeys;

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  //#endregion Properties & Signals

  //#region Constructor & Initialization
  
  constructor(private propertiesService: PropertiesService) {
    super();
    this.sortOptions = ListConstants.PROPERTIES_SORT_OPTIONS();
    this.orderBy = ListConstants.PROPERTIES_ORDERBY_DEFAULT();
    this.createSkeletonProperties();

    // Re-emit whenever the picked/excluded set or the matching total changes.
    effect(() => {
      this.selectedRecipients();
      this.totalMatching();
      this.emitState();
    });
  }

  private emitState(): void {
    // Include the active search term so select-all resolves exactly the set shown in the list.
    this.selectionChanged.emit(this.buildSelectionState({ ...this.propertiesFilter, searchTerm: this.searchTerm }));
  }

  ngOnInit(): void {
    this.propertiesFilter = { ...this.initialFilter() };
    this.loadProperties(false);
  }

  private createSkeletonProperties(): void {
    this.skeletonProperties = Array.from({ length: this.take }, (_, index) => ({
      propertyId: index,
      organizationId: 0,
      title: 'Loading...',
      lotNumber: 'Loading...',
      memberCount: 0,
      status: PropertyStatuses.Active,
      addresses: [{
        addressId: -1,
        addressType: AddressTypes.Physical,
        primaryAddress: '123 Test St',
        city: 'Test City',
        stateProvince: { 
          stateProvinceId: 0,
          name: 'Test State',
          abbreviation: 'TS',
          countryId: 0
        },
        primaryZip: '12345',
        country: {
          countryId: 0,
          name: 'Test Country',
          alpha2Code: 'TC',
          alpha3Code: 'TCO',
          numericCode: '000',
         },
        verificationStatus: AddressVerificationStatus.Unverified,
      }],
      propertyMembers: []
    } as PropertyModel));
  }

  //#endregion Constructor & Initialization

  //#region Data Loading

  public get displayProperties(): PropertyModel[] {
    const propertyListValue = this.propertyList();
    if (this.isLoadingProperties && (!propertyListValue || propertyListValue.length === 0)) {
      return this.skeletonProperties;
    }
    return propertyListValue || [];
  }

  public async loadProperties(append: boolean = false): Promise<void> {
    const orgId = this.organizationId();
    if (!orgId) {
      return;
    }

    this.isLoadingProperties = true;
    
    if (append === false) {
      this.resetPagination();
    }
    
    try {
      this.propertiesFilter.organizationIds = [orgId];

      const result = await this.propertiesService.searchProperties(
        {...this.propertiesFilter, searchTerm: this.searchTerm},
        this.orderBy,
        this.skip(),
        this.take,
        true
      );

      if (result.success && result.data) {
        if (append === true) {
          this.propertyList.set(this.propertyList().concat(result.data.data || []));
        } else {
          // NOTE: selection is intentionally NOT cleared here — picks accumulate across searches/filters.
          this.propertyList.set(result.data.data || []);
        }
        this.totalCount = result.data.total || 0;
        this.totalMatching.set(result.data.total || 0);
        this.hasNext = result.data.hasNext || false;
      } else {
        this.propertyList.set([]);
        this.totalCount = 0;
        this.totalMatching.set(0);
        this.hasNext = false;
      }
    } catch (error) {
      this.propertyList.set([]);
      this.totalCount = 0;
      this.totalMatching.set(0);
      this.hasNext = false;
    } finally {
      this.isLoadingProperties = false;
      // Cover filter changes where the matching total happens to be unchanged (effect wouldn't re-fire).
      this.emitState();
    }
  }

  public async loadMore(): Promise<void> {
    this.loadProperties(true);
  }

  public onFilterApplied(filter: PropertiesFilter): void {
    this.propertiesFilter = { ...filter };
    this.resetExclusionsForNewResultSet();
    this.loadProperties(false);
  }

  //#endregion Data Loading

  //#region Formatting Methods

  public formatOwners(property: PropertyModel): string {
    if (!property.propertyMembers || property.propertyMembers.length === 0) {
      return '-';
    }

    const owners = property.propertyMembers
      .filter(m => m.memberType === PropertyMemberTypes.Owner)
      .sort((a, b) => {
        const lastNameCompare = a.lastName.localeCompare(b.lastName);
        if (lastNameCompare !== 0) return lastNameCompare;
        return a.firstName.localeCompare(b.firstName);
      })
      .map(m => `${m.firstName} ${m.lastName}`);

    return owners.length > 0 ? owners.join(', ') : '-';
  }

  public formatOwnersLimited(property: PropertyModel): string {
    if (!property.propertyMembers || property.propertyMembers.length === 0) {
      return '-';
    }

    const owners = property.propertyMembers
      .filter(m => m.memberType === PropertyMemberTypes.Owner)
      .sort((a, b) => {
        const lastNameCompare = a.lastName.localeCompare(b.lastName);
        if (lastNameCompare !== 0) return lastNameCompare;
        return a.firstName.localeCompare(b.firstName);
      })
      .map(m => `${m.firstName} ${m.lastName}`);

    if (owners.length === 0) {
      return '-';
    }

    if (owners.length <= 2) {
      return owners.join(', ');
    }

    return `${owners.slice(0, 2).join(', ')}...`;
  }

  //#endregion Formatting Methods

  //#region Sorting & Pagination

  public onSearch(): void {
    this.resetExclusionsForNewResultSet();
    this.loadProperties(false);
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.resetExclusionsForNewResultSet();
    this.loadProperties(false);
  }

  // Select-all exclusions are scoped to the filter/search they were made under; once the matching set
  // changes those exclusions are meaningless and would wrongly subtract from the new total, so drop them.
  // Pick-mode selections are intentionally preserved so they keep accumulating across searches. Sorting
  // doesn't change the set, so it deliberately does not reset.
  private resetExclusionsForNewResultSet(): void {
    if (this.isSelectAll) {
      this.selectedRecipients.set(new Map());
    }
  }

  public onSortChange(value: string): void {
    this.orderBy = value;
    this.loadProperties(false);
  }

  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [PropertyListSortOptions, PropertyListSortOptions]> = {
      "Property Title": [PropertyListSortOptions.TitleASC, PropertyListSortOptions.TitleDESC],
      "Address": [PropertyListSortOptions.AddressASC, PropertyListSortOptions.AddressDESC],
      "Lot Number": [PropertyListSortOptions.LotNumberASC, PropertyListSortOptions.LotNumberDESC],
      "Owner": [PropertyListSortOptions.TitleASC, PropertyListSortOptions.TitleDESC]
    };

    const [ascOption, descOption] = columnSortMap[columnName]
      || [PropertyListSortOptions.TitleASC, PropertyListSortOptions.TitleDESC];

    let sortDirection: PropertyListSortOptions;

    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }

    this.orderBy = sortDirection;
    this.loadProperties(false);
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  //#endregion Sorting & Pagination

  //#region Selection Management

  public onPropertyToggle(propertyId: number, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.onToggle({ id: propertyId, checked: checkbox.checked });
  }

  public onToggle(data: { id: number; checked: boolean }): void {
    const property = this.propertyList().find(p => p.propertyId === data.id);
    this.toggleSelection(data.id, property ? this.propertyLabel(property) : `#${data.id}`, data.checked);
  }

  private propertyLabel(property: PropertyModel): string {
    return property.title || property.lotNumber || `#${property.propertyId}`;
  }

  //#endregion Selection Management

}
